import { c as _c } from "react-compiler-runtime";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type Control, type FieldValues, type Path, useController } from "react-hook-form";
import { ErrorMessage, HintMessage, Label, useFieldErrorVisibility, useFieldIds } from "@/components/form";
import { Text } from "@/components/primitives";
import { cn } from "@/core/utils";
export type CountryFieldOption = {
  readonly label: string;
  readonly value: string;
};
function normalizeSupportedCountryValue(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toUpperCase().replace(/\s+/g, "");
  if (normalized === "US" || normalized === "USA" || normalized === "UNITEDSTATES") {
    return "US";
  }
  if (normalized === "CA" || normalized === "CANADA") {
    return "CA";
  }
  return null;
}
type FieldAppearance = "default" | "signup";
type RHFProps<T extends FieldValues> = {
  readonly appearance?: FieldAppearance;
  readonly name: Path<T>;
  readonly control?: Control<T>;
  readonly label: string;
  readonly hint?: string;
  readonly placeholder?: string;
  readonly options?: readonly CountryFieldOption[];
  readonly className?: string;
  readonly menuClassName?: string;
};
function RHF(t0) {
  const $ = _c(68);
  const {
    appearance: t1,
    name,
    control,
    label,
    hint,
    placeholder,
    options: t2,
    className,
    menuClassName
  } = t0;
  const appearance = t1 === undefined ? "default" : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const options = t3;
  let t4;
  if ($[2] !== control || $[3] !== name) {
    t4 = {
      name,
      control
    };
    $[2] = control;
    $[3] = name;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    field,
    fieldState
  } = useController(t4);
  const fieldIds = useFieldIds(name);
  const menuRef = useRef(null);
  const [isMenuOpen, setMenuOpen] = useState(false);
  let t5;
  if ($[5] !== field.value) {
    t5 = normalizeSupportedCountryValue(field.value);
    $[5] = field.value;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const normalizedValue = t5;
  let t6;
  if ($[7] !== normalizedValue || $[8] !== options) {
    t6 = options.find(option => option.value === normalizedValue) ?? null;
    $[7] = normalizedValue;
    $[8] = options;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const selectedCountry = t6;
  const showError = useFieldErrorVisibility(fieldState, control);
  let t7;
  let t8;
  if ($[10] !== field || $[11] !== normalizedValue) {
    t7 = () => {
      if (!normalizedValue || normalizedValue === field.value) {
        return;
      }
      field.onChange(normalizedValue);
    };
    t8 = [field, normalizedValue];
    $[10] = field;
    $[11] = normalizedValue;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const handleClickOutside = event => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          setMenuOpen(false);
        }
      };
      window.addEventListener("mousedown", handleClickOutside);
      return () => window.removeEventListener("mousedown", handleClickOutside);
    };
    t10 = [];
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  let t11;
  if ($[16] !== field) {
    t11 = country => {
      field.onChange(country.value);
      setMenuOpen(false);
    };
    $[16] = field;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const handleCountrySelect = t11;
  let t12;
  if ($[18] !== fieldIds.inputId || $[19] !== fieldIds.labelId || $[20] !== label || $[21] !== showError) {
    t12 = <Label htmlFor={fieldIds.inputId} id={fieldIds.labelId} invalid={showError} text={label} />;
    $[18] = fieldIds.inputId;
    $[19] = fieldIds.labelId;
    $[20] = label;
    $[21] = showError;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = `${fieldIds.hintId} ${fieldIds.errorId}`;
  const t14 = showError || undefined;
  const t15 = `${label} country`;
  const t16 = showError && "border-destructive ring-destructive ring-1";
  let t17;
  if ($[23] !== className || $[24] !== t16) {
    t17 = cn("border-input bg-card text-foreground type-body-sm flex h-12 w-full items-center gap-2 rounded border px-3", className, t16);
    $[23] = className;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== field) {
    t18 = () => {
      field.onBlur();
    };
    $[26] = field;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setMenuOpen(_temp);
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const t20 = selectedCountry?.label ?? placeholder ?? "";
  const t21 = selectedCountry ? "foreground" : "muted";
  let t22;
  if ($[29] !== t20 || $[30] !== t21) {
    t22 = <div className="min-w-0 flex-1 text-left"><Text component="span" text={t20} tone={t21} variant="body-sm" /></div>;
    $[29] = t20;
    $[30] = t21;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  let t24;
  if ($[33] !== fieldIds.inputId || $[34] !== isMenuOpen || $[35] !== t13 || $[36] !== t14 || $[37] !== t15 || $[38] !== t17 || $[39] !== t18 || $[40] !== t22) {
    t24 = <button aria-describedby={t13} aria-expanded={isMenuOpen} aria-haspopup="listbox" aria-invalid={t14} aria-label={t15} className={t17} id={fieldIds.inputId} type="button" onBlur={t18} onClick={t19}>{t22}{t23}</button>;
    $[33] = fieldIds.inputId;
    $[34] = isMenuOpen;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
    $[38] = t17;
    $[39] = t18;
    $[40] = t22;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== handleCountrySelect || $[43] !== isMenuOpen || $[44] !== menuClassName || $[45] !== options || $[46] !== selectedCountry?.value) {
    t25 = isMenuOpen ? <div className={cn("border-input bg-card shadow-elevated absolute top-full left-0 z-20 mt-1 w-full overflow-y-auto rounded border", menuClassName)} role="listbox">{options.map(country_0 => <button aria-selected={country_0.value === selectedCountry?.value} className="hover:bg-muted/60 flex w-full items-center gap-2 px-3 py-2.5 text-left" data-testid={`CountryFieldOption-${country_0.value.toLowerCase()}`} key={country_0.value} role="option" type="button" onClick={() => handleCountrySelect(country_0)}><Text component="span" text={country_0.label} tone="foreground" variant="body-sm" /></button>)}</div> : null;
    $[42] = handleCountrySelect;
    $[43] = isMenuOpen;
    $[44] = menuClassName;
    $[45] = options;
    $[46] = selectedCountry?.value;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t24 || $[49] !== t25) {
    t26 = <div className="relative" ref={menuRef}>{t24}{t25}</div>;
    $[48] = t24;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  const t27 = showError ? "pt-1" : appearance === "signup" ? undefined : "h-2";
  let t28;
  if ($[51] !== fieldIds.hintId || $[52] !== hint || $[53] !== showError) {
    t28 = hint && !showError ? <HintMessage id={fieldIds.hintId} text={hint} /> : null;
    $[51] = fieldIds.hintId;
    $[52] = hint;
    $[53] = showError;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== fieldIds.errorId || $[56] !== fieldIds.inputId || $[57] !== fieldState || $[58] !== showError) {
    t29 = showError && fieldState.error?.message ? <ErrorMessage htmlFor={fieldIds.inputId} id={fieldIds.errorId} text={fieldState.error.message} /> : null;
    $[55] = fieldIds.errorId;
    $[56] = fieldIds.inputId;
    $[57] = fieldState;
    $[58] = showError;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== t27 || $[61] !== t28 || $[62] !== t29) {
    t30 = <div className={t27}>{t28}{t29}</div>;
    $[60] = t27;
    $[61] = t28;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== t12 || $[65] !== t26 || $[66] !== t30) {
    t31 = <div className="CountryField flex flex-col gap-1 text-left" data-testid="CountryField">{t12}{t26}{t30}</div>;
    $[64] = t12;
    $[65] = t26;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  return t31;
}
function _temp(previous) {
  return !previous;
}
export const CountryField = {
  RHF
};